export type TableValue = string | number | null;

export type TableFilterMode =
  | "all"
  | "filled"
  | "empty"
  | "gt0"
  | "eq0"
  | "number_gt"
  | "number_lt"
  | "number_eq"
  | "number_between"
  | "text_contains"
  | "text_not_contains"
  | "text_starts_with";

export type TableValueFilter<Key extends string = string> = {
  key: Key;
  mode: TableFilterMode;
  value?: string;
  min?: string;
  max?: string;
  selectedValues?: string[];
  excludedValues?: string[];
};

export function tableValueIsFilled(value: TableValue): boolean {
  if (value == null) return false;
  if (typeof value === "number") return Number.isFinite(value);
  return value.trim().length > 0;
}

export function tableFacetValue(value: TableValue): string {
  if (value == null) return "";
  return String(value).slice(0, 160);
}

export function tableFacetLabel(value: string): string {
  return value || "Пусто";
}

export function parseTableFilterNumber(value: string | undefined): number | null {
  if (!value) return null;
  const normalized = value.replace(/\s/g, "").replace(",", ".");
  const numberValue = Number(normalized);
  return Number.isFinite(numberValue) ? numberValue : null;
}

export function tableValueMatchesFilter<Key extends string>(
  value: TableValue,
  filter: TableValueFilter<Key>,
  options?: { ignoreValueSelection?: boolean },
): boolean {
  const numberValue = typeof value === "number" ? value : Number(value);
  if (!options?.ignoreValueSelection) {
    const valueKey = tableFacetValue(value);
    if (Array.isArray(filter.selectedValues) && !filter.selectedValues.includes(valueKey)) return false;
    if (!Array.isArray(filter.selectedValues) && Array.isArray(filter.excludedValues) && filter.excludedValues.includes(valueKey)) return false;
  }
  if (filter.mode === "all") return true;
  if (filter.mode === "filled") return tableValueIsFilled(value);
  if (filter.mode === "empty") return !tableValueIsFilled(value);
  if (filter.mode === "gt0") return Number.isFinite(numberValue) && numberValue > 0;
  if (filter.mode === "eq0") return Number.isFinite(numberValue) && numberValue === 0;
  if (filter.mode === "number_gt") {
    const compare = parseTableFilterNumber(filter.value);
    if (compare == null) return true;
    return Number.isFinite(numberValue) && numberValue > compare;
  }
  if (filter.mode === "number_lt") {
    const compare = parseTableFilterNumber(filter.value);
    if (compare == null) return true;
    return Number.isFinite(numberValue) && numberValue < compare;
  }
  if (filter.mode === "number_eq") {
    const compare = parseTableFilterNumber(filter.value);
    if (compare == null) return true;
    return Number.isFinite(numberValue) && numberValue === compare;
  }
  if (filter.mode === "number_between") {
    const min = parseTableFilterNumber(filter.min);
    const max = parseTableFilterNumber(filter.max);
    if (min == null && max == null) return true;
    if (!Number.isFinite(numberValue)) return false;
    if (min != null && numberValue < min) return false;
    if (max != null && numberValue > max) return false;
    return true;
  }
  if (filter.mode === "text_contains" || filter.mode === "text_not_contains" || filter.mode === "text_starts_with") {
    const needle = (filter.value ?? "").trim().toLocaleLowerCase("ru");
    if (!needle) return true;
    const text = value == null ? "" : String(value).toLocaleLowerCase("ru");
    if (filter.mode === "text_contains") return text.includes(needle);
    if (filter.mode === "text_not_contains") return !text.includes(needle);
    return text.startsWith(needle);
  }
  return true;
}

export function tableValuesMatchFilters<Key extends string>(
  getValue: (key: Key) => TableValue,
  filters: TableValueFilter<Key>[],
  options?: { ignoreValueSelectionForKey?: Key },
): boolean {
  for (const filter of filters) {
    const value = getValue(filter.key);
    if (!tableValueMatchesFilter(value, filter, { ignoreValueSelection: filter.key === options?.ignoreValueSelectionForKey })) return false;
  }
  return true;
}

export function compareTableValues(a: TableValue, b: TableValue, dir: "asc" | "desc"): number {
  const direction = dir === "asc" ? 1 : -1;
  const aEmpty = !tableValueIsFilled(a);
  const bEmpty = !tableValueIsFilled(b);
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  const aNumber = typeof a === "number" ? a : Number(a);
  const bNumber = typeof b === "number" ? b : Number(b);
  if (Number.isFinite(aNumber) && Number.isFinite(bNumber)) return (aNumber - bNumber) * direction;
  return String(a).localeCompare(String(b), "ru", { numeric: true }) * direction;
}
